import React, { Fragment, useState } from 'react';
import qs from 'query-string';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { addDays, differenceInCalendarDays, getDay, getDayOfYear, isAfter, isSameDay, isFuture } from 'date-fns';

import { BookingBar, ButtonsRow, Calendar, Divider, LoadingContainer, MainButton, MainForm, MainFormContent, NormalPopup, Paragraph, SelectionBoxInput, TitleContainer } from '@beacharound/beacharound-ui';
import { trackAddToCart, formatDate } from '@beacharound/beacharound-ui-helpers';

import { createReservation } from '../../../api/reservations/beaches';
import { getBeachDates, getBeachMap } from '../../../api/beach';
import { getProducts } from '../../../api/products';

export default function BookingCalendar({ beach, hideSeasonal }) {

    const history = useHistory();
    const { beachSlug } = useParams();
    const { search } = useLocation();
    const parsedSearch = qs.parse(search);
    const [bookingErrorText, setBookingErrorText] = useState();
    const [isSearching, setIsSearching] = useState();
    const [notFoundPopup, setNotFoundPopup] = useState();
    const [halfDayPopup, setHalfDayPopup] = useState();
    const [popupStatus, setPopupStatus] = useState();
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();

    // let seasonalQuery = qs.stringify({
    //     ...(qs.parse(search)),
    //     seasonal: true,
    //     placedProduct: undefined,
    //     durationType: undefined
    // })

    const manualBooking = beach?.bookingType === "MANUAL" || !beach?.map;

    const cache = useQueryClient();
    const { mutateAsync: create, status, error, reset } = useMutation(createReservation, {
        onSuccess: (data, variables) => {
            trackAddToCart({
                _id: beach?.name,
                name: "reservation",
                type: "beach",
                activity: beach?.name,
            });
            cache.invalidateQueries(["reservations", data?._id]);

            let payloadSearch = parsedSearch;

            if (variables?.data?.durationType) {
                payloadSearch = {
                    ...payloadSearch,
                    durationType: variables.data.durationType
                };
            } else {
                payloadSearch = {
                    ...payloadSearch,
                    durationType: undefined
                };
            }
            payloadSearch = {
                ...payloadSearch,
                seasonal: undefined,
                placedProduct: undefined
            };
            setPopupStatus();
            setHalfDayPopup();

            if (!!manualBooking) {
                if (parsedSearch.widget === "true") {
                    return window.top.location.replace(`/prenotazione/spiaggia/${beachSlug}/${data?._id}/ombrellone?${qs.stringify(payloadSearch)}`);
                }
                return history.push(`/prenotazione/spiaggia/${beachSlug}/${data?._id}/ombrellone?${qs.stringify(payloadSearch)}`);
            }

            if (parsedSearch.widget === "true") {
                return window.top.location.replace(`/prenotazione/spiaggia/${beachSlug}/${data?._id}/mappa?${qs.stringify(payloadSearch)}`);
            }
            return history.push(`/prenotazione/spiaggia/${beachSlug}/${data?._id}/mappa?${qs.stringify(payloadSearch)}`);
        }
    });

    const { data: dates, isLoading: isLoadingBeachDates } = useQuery(["beaches", beach?.slug, "dates"], () =>
        getBeachDates({ slug: beach?.slug }),
        { enabled: !!beach?.slug }
    );
    const options = {
        limit: 200,
        params: { bundleType: 'seasonal' }
    };
    const { data: seasonalBundles, isLoading: isLoadingSeasonalBundles } = useQuery(["beaches", beach?.slug, "products", "seasonal", options], () =>
        getProducts({ activitySlug: beach?.slug, ...options }),
        { enabled: !!beach?.slug }
    );

    const isLoading = isLoadingBeachDates;

    const orderedDates = dates?.sort((a, b) => new Date(a.date) - new Date(b.date));
    const availableDates = orderedDates?.filter(({ status }) => status !== "SOLDOUT").map(({ date }) => new Date(date));
    const startDayOfWeek = typeof beach?.purchasability?.startDay === "number" && beach?.purchasability?.startDay >= 0 ? beach?.purchasability?.startDay : undefined;
    const endDayOfWeek = typeof beach?.purchasability?.endDay === "number" && beach?.purchasability?.endDay >= 0 ? beach?.purchasability?.endDay : undefined;
    const startDateOfYear = startDate && getDayOfYear(new Date(startDate));

    const isStartDayOfWeek = startDayOfWeek !== undefined && startDayOfWeek !== false;
    const isEndDayOfWeek = endDayOfWeek !== undefined && endDayOfWeek !== false;

    const excludeDates = orderedDates?.filter(({ date, status }) => {
        const dayOfWeek = getDay(new Date(date));
        const dayOfYear = getDayOfYear(new Date(date));
        if (isStartDayOfWeek && (!startDate || (startDate && endDate))) {
            return dayOfWeek !== startDayOfWeek;
        }
        if (isStartDayOfWeek && !isEndDayOfWeek && startDateOfYear && !endDate) {
            return dayOfYear <= startDateOfYear && dayOfWeek !== startDayOfWeek;
        }
        if (isStartDayOfWeek && isEndDayOfWeek && startDateOfYear && !endDate) {
            return (dayOfYear <= startDateOfYear && dayOfWeek !== startDayOfWeek) || (dayOfYear >= startDateOfYear && dayOfWeek !== endDayOfWeek);
        }
        if (isEndDayOfWeek && startDate) {
            return dayOfWeek !== endDayOfWeek;
        }
        let isSoldout = status === "SOLDOUT";
        // if (beach?.hasMap) {
        return isSoldout;
        // }
        // let hasProducts = true;
        // if (!!startDate) {
        //     const selectedDay = orderedDates.find(({ date }) => isSameDay(new Date(date), new Date(startDate)));
        //     const selectedProducts = selectedDay?.products;
        //     hasProducts = selectedProducts?.some(product => products?.includes(product));
        // }
        // if (!!endDate) {
        //     hasProducts = true;
        // }
        // return isSoldout || !hasProducts;
    }).map(({ date }) => new Date(date));

    let minDate = new Date();
    if (beach?.availability?.fromDate && isAfter(new Date(beach?.availability?.fromDate), new Date())) {
        minDate = new Date(beach?.availability?.fromDate);
    }

    if (availableDates?.[0]) {
        minDate = availableDates[0];
    }

    let maxDate = availableDates?.[availableDates?.length - 1] ? availableDates[availableDates.length - 1] : (beach?.availability?.toDate && new Date(beach?.availability?.toDate));

    if (beach?.purchasability?.skip) {
        minDate = addDays(new Date(minDate), beach?.purchasability?.skip);
    }

    if (beach?.purchasability?.apertures) {
        maxDate = addDays(new Date(minDate), beach?.purchasability?.apertures - 1);
    }

    let resetMinDate = minDate;
    let resetMaxDate = maxDate;

    if (beach?.purchasability?.minDuration && startDate) {
        minDate = addDays(new Date(startDate), beach?.purchasability?.minDuration - 1);
    }

    if (beach?.purchasability?.maxDuration && startDate) {
        maxDate = addDays(new Date(startDate), beach?.purchasability?.maxDuration - 1);
    }

    if (startDate && endDate) {
        minDate = resetMinDate;
        maxDate = resetMaxDate;
    }

    const onChange = dates => {
        const [start, end] = dates;

        reset();
        setBookingErrorText();

        setStartDate(start);
        return setEndDate(end);
    }

    async function nextStep(data) {
        try {
            let selectedStartDate = startDate || minDate;
            let selectedEndDate = endDate || startDate || minDate;

            if (!selectedStartDate || !selectedEndDate) {
                return setBookingErrorText("Non hai ancora inserito le date");
            }

            if (isAfter(selectedStartDate, selectedEndDate)) {
                return setBookingErrorText("La data di checkout non può essere prima di quella di checkin");
            }

            if (beach?.purchasability?.minDuration > 0) {
                const duration = differenceInCalendarDays(new Date(selectedEndDate), new Date(selectedStartDate)) + 1;
                if (duration < beach?.purchasability?.minDuration) {
                    return setBookingErrorText(`ATTENZIONE! Questa spiaggia accetta prenotazioni di almeno ${beach?.purchasability?.minDuration} ${beach?.purchasability?.minDuration > 1 ? "giorni" : "giorno"}`);
                }
            }

            if (beach?.purchasability?.maxDuration > 0) {
                const duration = differenceInCalendarDays(new Date(selectedEndDate), new Date(selectedStartDate)) + 1;
                if (duration > beach?.purchasability?.maxDuration) {
                    return setBookingErrorText(`ATTENZIONE! Questa spiaggia accetta prenotazioni di massimo ${beach?.purchasability?.minDuration} ${beach?.purchasability?.minDuration > 1 ? "giorni" : "giorno"}`);
                }
            }

            if (beach?.hasHalfDay && !data?.durationType && ((startDate && !endDate) || isSameDay(new Date(startDate), new Date(endDate)))) {
                return setHalfDayPopup(true);
            }

            if (beach?.hasHalfDay && beach?.halfDay?.multi && !data?.durationType) {
                return setHalfDayPopup(true);
            }

            setPopupStatus('loading')
            setIsSearching(true)

            if (beach?.map && beach?.bookingType !== "MANUAL") {
                const beachMapOptions = {
                    slug: beach?.slug,
                    beachMapId: beach?.map,
                    params: {
                        fromDate: formatDate(selectedStartDate, "yyyy-MM-dd"),
                        toDate: formatDate(selectedEndDate, "yyyy-MM-dd"),
                        durationType: data?.durationType
                    }
                };

                const beachMap = await getBeachMap(beachMapOptions);
                const hasBookableProducts = beachMap?.placedProducts?.some(({ bookable, relatedProduct }) => !!bookable && !relatedProduct?.isSlot && relatedProduct?.type?.type !== "cabin");

                if (!hasBookableProducts) {
                    setHalfDayPopup();
                    setPopupStatus();
                    setIsSearching();
                    return setNotFoundPopup(true);
                }
                cache.setQueryData(["beachMap", beachMapOptions], beachMap);
            }

            await create({
                beachSlug: beach?.slug,
                data: {
                    checkin: formatDate(selectedStartDate, "yyyy-MM-dd"),
                    checkout: formatDate(selectedEndDate, "yyyy-MM-dd"),
                    durationType: data?.durationType,
                    from: (parsedSearch.externalWebsiteUrl || parsedSearch.externalSocialLink) && "external"
                }
            });
        } catch (error) {
            setPopupStatus();
            console.log(error);
            throw error;
        }
    }

    function seasonalNextStep() {
        let payloadSearch = parsedSearch;
        payloadSearch = {
            ...payloadSearch,
            seasonals: true,
            placedProduct: undefined
        };

        if (parsedSearch.widget === "true") {
            return window.top.location.replace(`/prenotazione/spiaggia/${beachSlug}/seasonal/mappa?${qs.stringify(payloadSearch)}`);
        }
        return history.push(`/prenotazione/spiaggia/${beachSlug}/seasonal/mappa?${qs.stringify(payloadSearch)}`);
    }

    let bundles = !!seasonalBundles?.length ? seasonalBundles?.filter(({ toDate }) => !toDate || isFuture(new Date(toDate))) : [];
    const actualHour = new Date().getHours();
    const bookingFromHour = beach?.bookingFromHour || 7;
    const bookingToHour = beach?.bookingToHour || 22;
    const isBookingManualAvailable = actualHour >= bookingFromHour && actualHour < bookingToHour;

    if (beach?.bookingType === "MANUAL" && !isBookingManualAvailable) {
        return (
            <LoadingContainer isLoading={isLoading || isSearching || isLoadingSeasonalBundles} loadingText={'Stiamo cercando i posti disponibili...'}>
                <TitleContainer>
                    <Paragraph
                        center
                        color="red"
                        text={`<strong>Questa spiaggia è prenotabile dalle ore ${bookingFromHour < 10 ? "0" + String(bookingFromHour) : String(bookingToHour)}:00 alle ore ${bookingToHour < 10 ? "0" + String(bookingToHour) : String(bookingToHour)}:00</strong>`}
                    />
                </TitleContainer>
            </LoadingContainer>
        );
    }

    return (
        <LoadingContainer isLoading={isLoading || isSearching || isLoadingSeasonalBundles} loadingText="Stiamo cercando i posti disponibili...">

            {!beach?.onlySeasonal &&
                <Calendar
                    startDate={startDate}
                    onChange={onChange}
                    endDate={endDate}
                    minDate={minDate}
                    maxDate={maxDate}
                    excludeDates={excludeDates}
                />
            }

            {!beach?.onlySeasonal && startDate &&
                <BookingBar
                    text={bookingErrorText || error?.response?.data?.message || 'Scegli le date'}
                    action={nextStep}
                    status={status}
                    buttonText="AVANTI"
                    icon="arrow-right"
                />
            }

            {beach?.hybrid?.enabled &&
                <Fragment>
                    <Divider />
                    <TitleContainer lowMargin>
                        <Paragraph center text="Questa spiaggia prevede anche la prenotazione giornaliera di posti non attrezzati" />
                    </TitleContainer>
                    <ButtonsRow center>
                        <MainButton text="Riserva un posto non attrezzato" action={() => history.push(`/prenotazione/spiaggia-ibrida/${beach?.slug}/data`)} border />
                    </ButtonsRow>
                </Fragment>
            }

            {bundles?.length > 0 && !hideSeasonal && beach?.seasonal?.online &&
                <Fragment>
                    <TitleContainer>
                        <Paragraph center text={beach?.onlySeasonal ? "Al momento questa spiaggia prevede solo prenotazioni stagionali." : "Questa spiaggia prevede prezzi speciali per le prenotazioni stagionali!"} />
                    </TitleContainer>
                    <MainButton text="Prenota subito il tuo posto stagionale!" action={seasonalNextStep} border />
                </Fragment>
            }

            <NormalPopup
                visible={halfDayPopup}
                setVisible={setHalfDayPopup}
                title="Prenotazione"
            >
                <TitleContainer>
                    <Paragraph text="Questa spiaggia prevede anche prenotazioni per mezza giornata.<br/> Desideri prenotare per l'intera giornata o solo metà?" />
                </TitleContainer>

                <MainForm onSubmit={nextStep} constraints={{ durationType: { presence: true } }}>
                    <MainFormContent flex>
                        <SelectionBoxInput
                            label="Durata prenotazione giornaliera"
                            name="durationType"
                            options={[{
                                title: 'Intera giornata',
                                value: 'daily'
                            }, {
                                title: 'Mattina',
                                value: 'morning'
                            }, {
                                title: 'Pomeriggio',
                                value: 'afternoon'
                            }]}
                        />
                    </MainFormContent>

                    <Divider />

                    <ButtonsRow fill center>
                        <MainButton border text="Annulla" action={() => setHalfDayPopup()} />
                        <MainButton text="Conferma" submit status={popupStatus || status} />
                    </ButtonsRow>
                </MainForm>
            </NormalPopup>

            <NormalPopup
                visible={notFoundPopup}
                setVisible={setNotFoundPopup}
                title="Ooops.."
            >
                <TitleContainer>
                    <Paragraph text="Ci dispiace, non sono stati trovati prodotti disponibili per queste date.<br/> Prova con delle date diverse!" />
                </TitleContainer>

                <Divider />

                <ButtonsRow fill center>
                    <MainButton text="Ho capito" action={() => setNotFoundPopup()} />
                </ButtonsRow>
            </NormalPopup>
        </LoadingContainer>
    );
}